import React, {
  CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef,
} from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useAppSelector } from '../../../store/hooks';
import { IntersectionId } from '../../../types/primitives';
import { Direction } from '../../../types/Direction';
import { TIME_FORMAT } from '../../../util/constants';
import { Movement } from '../../../types/Movement';

// eslint-disable-next-line @typescript-eslint/ban-types
interface HeatmapPPDLauncherProps {
  style?: CSSProperties;
}

export interface HeatmapPPDLauncherHandle {
  open: (intersectionId: IntersectionId,
    time: string, direction: Direction, movement: Movement) => void;
}

// The number of minutes that should be queried
const PPD_MINUTES = 15;

/**
 * The point of this component is to open the PPD page.
 * I didn't want to add all the stuff we need from the store
 * into the heatmap component because I didn't want to re-render all the time.
 */
export const HeatmapPPDLauncher = forwardRef<HeatmapPPDLauncherHandle, HeatmapPPDLauncherProps>(
  (props, ref) => {
    // Grab stuff from the store
    const availableIntersections = useAppSelector(
      (state) => state.entities.pm.availableIntersections,
    );
    const startDate = useAppSelector((state) => state.entities.pm.startDate);
    const endDate = useAppSelector((state) => state.entities.pm.endDate);
    const timezoneMap = useAppSelector((state) => state.entities.pm.timezoneMap);
    const selectedDaysOfWeek = useAppSelector((state) => state.entities.pm.selectedDaysOfWeek);

    const timezoneMapRef = useRef(timezoneMap);

    useEffect(() => {
      timezoneMapRef.current = timezoneMap;
    }, [timezoneMap]);

    // This exposes the following function(s) to other components via forwarding the ref.
    useImperativeHandle(ref, () => ({
      open(intersectionId, time, direction, movement) {
        if (!intersectionId) {
          toast.error('No intersection ID!');
          return;
        }
        const timezone = timezoneMapRef.current[intersectionId];
        if (!timezone) {
          toast.error('No timezone!');
          return;
        }
        const encodedTimezone = encodeURIComponent(timezone);

        const intersection = availableIntersections
          .find((i) => i.intersectionId === intersectionId);
        if (!intersection) {
          toast.error('Couldn\'t resolve intersection!');
          return;
        }

        const todStart = time;
        const todEnd = moment(time, TIME_FORMAT)
          .add(PPD_MINUTES, 'minutes')
          .format(TIME_FORMAT);

        const dowString = selectedDaysOfWeek.map((d) => d.value)
          .join(',');
        window.open(`/ppd/${startDate}/${endDate}/${encodedTimezone}/${intersectionId}/${encodeURIComponent(
          intersection.intersectionName,
        )}/${direction}/${movement}/${dowString}/${todStart}/${todEnd}/${intersection.position.lat}/${intersection.position.lng}`,
        '_blank');
      },
    }));
    // eslint-disable-next-line react/prop-types
    return (<div style={{ ...props.style }} />);
  },
);
